import { useLocation } from '@gatsbyjs/reach-router';
import { Box } from '@mui/material';
import { compareAsc, isSameHour, isSameMinute } from 'date-fns/esm';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import NullStateDiv from '@components/NullStateDiv';

import * as ROUTES from '@constants/routes';

import { useGetRoutines } from '@hooks/api/routine';

import type { RoutineEvent } from '@models/routine';
import { RoutineRecord } from '@models/routine';

import { formatTime } from '@utils/format';

import AddRecord from '../AddRecord';
import { DashHeader } from '../Common.style';
import RoutineCard from '../RoutineCard';
import {
  Scroller,
  Wrapper,
  ViewAll,
  HeaderSpacing,
  CardWrapper,
} from './Routines.style';

const messages = defineMessages({
  title: {
    id: 'dashboardRoutines.title',
    defaultMessage: 'Routines',
  },
  noRoutines: {
    id: 'dashboardRoutines.noRoutines',
    defaultMessage:
      'No routines today. To see routines here, add a routine by clicking the button below or from within the Routines section.',
  },
  viewAll: {
    id: 'dashboardRoutines.viewAll',
    defaultMessage: 'View All',
  },
  add: {
    id: 'dashboardRoutines.add',
    defaultMessage: 'Add a routine',
  },
});

function Routines({
  orientation = 'scroll-x',
}: {
  orientation?: 'grid' | 'scroll-x';
}) {
  const location = useLocation();
  const { formatMessage } = useIntl();
  const { data: routines, loading } = useGetRoutines();
  const todaysRoutines = React.useMemo(() => {
    return RoutineRecord.getTodaysRoutines(routines);
  }, [routines]);
  const basePath = location.pathname;
  const isRoutinesPath = basePath === ROUTES.ROUTINES;
  const children = todaysRoutines
    .reduce<RoutineEvent[]>((acc, curr) => {
      // Only display 1 card per timeslot
      const match = acc.find(
        (routine) =>
          isSameHour(new Date(routine.startDate), new Date(curr.startDate)) &&
          (!(routine.endDate && curr.endDate) ||
            isSameMinute(new Date(routine.endDate), new Date(curr.endDate)))
      );
      if (!match) {
        acc.push(curr);
      }
      return acc;
    }, [])
    .sort((a, b) => compareAsc(new Date(a.startDate), new Date(b.startDate)))
    .map((routine, index) => {
      const startTime = new Date(routine.startDate);
      const endTime = routine.endDate ? new Date(routine.endDate) : undefined;
      const cardChild = (
        <RoutineCard
          endTime={
            endTime
              ? formatTime(endTime.getHours() * 100 + endTime.getMinutes())
              : undefined
          }
          key={`routine-card-${index}`}
          routines={todaysRoutines.filter(({ startDate, endDate }) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return (
              isSameHour(new Date(startDate), startTime) &&
              (!endDate || !endTime || isSameMinute(new Date(endDate), endTime))
            );
          })}
          startTime={formatTime(
            startTime.getHours() * 100 + startTime.getMinutes()
          )}
        />
      );
      return orientation === 'scroll-x' ? (
        <CardWrapper key={`routine-card-${index}`}>{cardChild}</CardWrapper>
      ) : (
        cardChild
      );
    });

  const noRoutinesCard =
    Object.keys(todaysRoutines).length === 0 ? (
      <NullStateDiv>
        {formatMessage(messages.noRoutines)}
        <AddRecord route={ROUTES.ROUTINES} text={formatMessage(messages.add)} />
      </NullStateDiv>
    ) : null;

  return (
    <Wrapper>
      <ContentLoader loading={loading}>
        <DashHeader>
          <HeaderSpacing>
            {!isRoutinesPath && (
              <>
                {formatMessage(messages.title)}
                <ViewAll to={ROUTES.ROUTINES}>
                  {formatMessage(messages.viewAll)}
                </ViewAll>
              </>
            )}
          </HeaderSpacing>
        </DashHeader>

        {orientation === 'scroll-x' && (
          <Scroller>
            {children}
            {noRoutinesCard}
          </Scroller>
        )}
        {orientation === 'grid' && (
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap' }}
          >
            {children}
            {noRoutinesCard}
          </Box>
        )}
      </ContentLoader>
    </Wrapper>
  );
}

export default withErrorBoundary(Routines, {
  fallback: null,
});
